import { apiRequest, ApiError } from "@/lib/api";
import { buildOrderWhatsappUrl } from "@/lib/order";
import type { PublicOrderPayload, PublicOrderResponse } from "@/types/order";
import type { CartItem } from "@/types/cart";
import { compositionKey } from "@/lib/food-configuration";
import type { PaymentChallenge } from "@/lib/retail-payment";

export type RetailPaymentMethod = PublicOrderPayload["payment_method"] | "whatsapp" | "mercado_pago_pix" | "mercado_pago_card" | "mercado_pago_boleto" | "mercado_pago_wallet";
export type RetailPayload = Omit<PublicOrderPayload, "payment_method"> & { payment_method: RetailPaymentMethod; replaces_checkout?: string };
export type RetailOrder = Omit<PublicOrderResponse, "payment_method" | "online_payment"> & {
  payment_method: RetailPaymentMethod;
  payment_method_label: string;
  cart_fingerprint: string;
  online_payment: { status: string; status_label?: string; amount: string; checkout_url: string; refunded_amount: string; can_resume?: boolean; is_terminal?: boolean; cart_edit_requested?: boolean; cart_edit_closed?: boolean; review_required?: boolean; pix_generated?: boolean; expires_at?: string | null; expiry_pending_confirmation?: boolean;
    flow?: "transparent" | "wallet"; preference_id?: string; provider?: "mercado_pago"; api_contract?: "/v1/orders"; max_installments?: number; installment_plan?: { installments: number; installment_amount: string; total_amount: string; installment_rate: string }; card_types?: string[]; selected_card_type?: string; available_methods?: RetailPaymentMethod[]; can_change_method?: boolean; can_start_payment?: boolean; boleto_url?: string; boleto_line?: string; boleto_barcode?: string; public_key?: string; available?: boolean; preview?: boolean; blocked_reason?: "test_credentials_required"; pix_code?: string; challenge?: PaymentChallenge | null } | null;
  company: { name: string; slug?: string; whatsapp: string; address: string; pickup_instructions: string; city?: string; logo_url?: string | null; primary_color?: string; is_verified?: boolean };
};
export type RetailSettings = {
  primary_color?: string;
  pickup_enabled: boolean; shipping_enabled: boolean; pickup_instructions: string;
  payments: Partial<Record<RetailPaymentMethod, boolean>>;
  payment_preview?: boolean;
};
export const retailPaymentLabels: Record<RetailPaymentMethod, string> = {
  whatsapp: "A combinar pelo WhatsApp", pix: "Pix direto da loja",
  card_on_delivery: "Cartão na retirada", cash_on_delivery: "Dinheiro na retirada",
  mercado_pago_pix: "Pix online pelo Mercado Pago", mercado_pago_card: "Cartão online pelo Mercado Pago",
  mercado_pago_boleto: "Boleto pelo Mercado Pago", mercado_pago_wallet: "Pagar com Mercado Pago",
};
export const retailFinancialLabels: Record<string, string> = {
  awaiting_buyer: 'Aguardando autorização no Mercado Pago',
  new: "Aguardando início do pagamento", creating: "Preparando pagamento", uncertain: "Pagamento em conciliação",
  ready: "Aguardando início do pagamento", pending: "Aguardando confirmação", paid: "Pagamento confirmado",
  rejected: "Pagamento recusado", cancelled: "Pagamento cancelado", expired: "Pagamento expirado", refunded: "Pagamento estornado",
  partially_refunded: "Pagamento parcialmente estornado", charged_back: "Pagamento contestado",
  in_mediation: "Pagamento em análise de contestação",
};
export const getRetailSettings = (slug: string) => apiRequest<RetailSettings>(`/retail/public/${encodeURIComponent(slug)}/settings/`, { cache: "no-store" });
export const getRetailOrder = (token: string, signal?: AbortSignal) => apiRequest<RetailOrder>(`/retail/public/orders/${encodeURIComponent(token)}/`, { cache: "no-store", signal });
export const startRetailPayment = (token: string) => apiRequest<RetailOrder>(`/retail/public/orders/${encodeURIComponent(token)}/payment/`, { method: "POST" });
export const authorizeRetailWallet = (token: string) => apiRequest<{ authorized: boolean }>(`/retail/public/orders/${encodeURIComponent(token)}/payment/`, {
  method: 'POST', body: JSON.stringify({ authorize_wallet: true }),
});
export type RetailCartLine = { product: number | null; variation: number | null; quantity: number; unit_price: string;
  options: { option_id: number; fraction: string }[]; observation: string };
export type RetailCartIntent = { confirm_change: true; base_fingerprint: string; items: RetailCartLine[]; total?: string };
export const editRetailAttempt = (token: string, intent: RetailCartIntent) => {
  if (!intent?.confirm_change || !/^[0-9a-f]{64}$/.test(intent.base_fingerprint ?? ""))
    throw new ApiError("Compra desatualizada", 409, { detail: "Atualize a compra antes de alterar os itens." });
  return apiRequest<RetailOrder>(`/retail/public/orders/${encodeURIComponent(token)}/cart/`, { method: "POST", body: JSON.stringify(intent) });
};
export const syncRetailPayment = (token: string, paymentId?: string) => apiRequest<RetailOrder>(`/retail/public/orders/${encodeURIComponent(token)}/sync/`, {
  method: "POST", ...(paymentId && /^[0-9]{1,160}$/.test(paymentId) ? { body: JSON.stringify({ payment_id: paymentId }) } : {}),
});
export const changeRetailPaymentMethod = (order: RetailOrder, method: RetailPaymentMethod) => apiRequest<RetailOrder>(
  `/retail/public/orders/${encodeURIComponent(order.tracking_token)}/payment/method/`,
  { method: 'POST', body: JSON.stringify({ method, expected_method: order.payment_method, confirm: true }) },
);
export const cancelRetailPayment = (orderId: number) => apiRequest(`/retail/payments/cancel/`, {
  method: "POST", authenticated: true, body: JSON.stringify({ order_id: orderId, confirm: true }),
});

export function retailPaymentStateLabel(payment: { status: string; status_label?: string; pix_generated?: boolean; expiry_pending_confirmation?: boolean }) {
  return payment.status_label ?? (payment.expiry_pending_confirmation ? "Validade do Pix encerrada — aguardando confirmação"
    : payment.pix_generated ? "Pix gerado — aguardando pagamento" : retailFinancialLabels[payment.status] ?? "Pagamento em análise");
}

export function retailCheckoutToken(path: string | undefined) {
  return path?.match(/^\/(?:loja|homologacao\/varejo)\/pedido\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i)?.[1];
}

export function retailStoreHref(slug: string) {
  return typeof window !== "undefined" && window.location.hostname === "dev.fornecedor.net"
    && window.location.pathname.startsWith("/homologacao/varejo") && slug === "homologacao-orders"
    ? "/homologacao/varejo" : `/${encodeURIComponent(slug)}`;
}
export function canResumeRetailPayment(order: RetailOrder) {
  const payment = order.online_payment;
  return Boolean(order.status !== "cancelled" && payment && !payment.cart_edit_requested && !payment.review_required && !payment.expiry_pending_confirmation && (payment.can_resume ??
    ["new", "ready", "pending", "rejected"].includes(payment.status)));
}
export function isRetailPaymentClosed(order: RetailOrder) {
  return order.status === "cancelled" || Boolean(order.online_payment && (order.online_payment.is_terminal ??
    ["cancelled", "expired", "refunded", "charged_back"].includes(order.online_payment.status)));
}

export function retailOrderLines(order: Pick<RetailOrder, "items">): RetailCartLine[] {
  return order.items.map(item => ({ product: item.product, variation: item.variation, quantity: item.quantity,
    unit_price: String(item.unit_price), options: (item.option_selections ?? []).map(o => ({ option_id: o.option_id, fraction: String(o.fraction ?? "1") })), observation: item.observation ?? "" }));
}
export function retailCartLines(items: CartItem[]): RetailCartLine[] {
  return items.map(item => ({ product: item.product.id, variation: item.variation?.id ?? null, quantity: item.quantity,
    unit_price: String(item.quoted_unit_price ?? item.variation?.current_price ?? item.product.current_price),
    options: (item.selected_options ?? []).map(o => ({ option_id: o.option_id, fraction: String(o.fraction ?? "1") })), observation: item.observation ?? "" }));
}
function lineKey(lines: RetailCartLine[], prices: boolean) {
  return JSON.stringify(lines.map(row => JSON.stringify([row.product, row.variation, row.quantity,
    prices ? Number(row.unit_price) : null, compositionKey(row.options), row.observation.trim()])).sort());
}
export function retailCartMatchesOrder(items: CartItem[], order: RetailOrder) {
  return lineKey(retailCartLines(items), true) === lineKey(retailOrderLines(order), true);
}
export function retailCartSameContents(items: CartItem[], order: RetailOrder) {
  return lineKey(retailCartLines(items), false) === lineKey(retailOrderLines(order), false);
}
export function retailIntent(order: RetailOrder, items: RetailCartLine[], total?: string): RetailCartIntent {
  return { confirm_change: true, base_fingerprint: order.cart_fingerprint, items, ...(total === undefined ? {} : { total }) };
}

export function retailError(error: unknown): string {
  if (error instanceof ApiError && error.data && typeof error.data === "object") {
    for (const value of Object.values(error.data)) {
      if (typeof value === "string") return value;
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    }
  }
  return "Não foi possível concluir a solicitação. Confira sua conexão e tente novamente.";
}

export function retailWhatsappUrl(order: RetailOrder, origin: string) {
  // Reutiliza o resumo existente; adapta apenas rótulos/estado do Varejo.
  const url = new URL(buildOrderWhatsappUrl(order.company.whatsapp, order.company.name,
    order as unknown as PublicOrderResponse, origin));
  let message = url.searchParams.get("text") ?? "";
  message = message.replace("Cartão na entrega", "Cartão na retirada").replace("Dinheiro na entrega", "Dinheiro na retirada");
  if (order.payment_method === "whatsapp") message += "\nForma de pagamento: A combinar pelo WhatsApp";
  message += "\nSituação: Aguardando confirmação da loja. O envio desta mensagem não confirma pagamento nem aceite.";
  url.searchParams.set("text", message);
  return url.toString();
}

type Attempt = { content: string; key: string; path?: string; editing?: boolean; editing_source?: "item_action"; predecessor?: string };
const attempts = new Map<string, Attempt>();
const volatileAttempts = new Set<string>();
const storageKey = (slug: string) => `retail-checkout:${slug}`;
export function forgetRetailCheckout(slug: string) {
  attempts.delete(slug);
  try { localStorage.removeItem(storageKey(slug)); volatileAttempts.delete(slug); } catch { volatileAttempts.add(slug); }
}
function readAttempt(slug: string): Attempt | null {
  let attempt: Attempt | null;
  if (volatileAttempts.has(slug)) attempt = attempts.get(slug) ?? null;
  else {
    try { attempt = JSON.parse(localStorage.getItem(storageKey(slug)) ?? "null") ?? attempts.get(slug) ?? null; }
    catch { attempt = attempts.get(slug) ?? null; }
  }
  // Legacy clients also set editing during recovery. That flag alone never
  // establishes an item edit; durable server revisions remain authoritative.
  if (attempt?.editing && attempt.editing_source !== "item_action") delete attempt.editing;
  return attempt;
}
export function rememberRetailOrder(order: RetailOrder) {
  const slug = order.company.slug, token = retailCheckoutToken(order.tracking_path);
  if (!slug || !token || token !== order.tracking_token || !order.online_payment || order.online_payment.status === "paid") return false;
  const old = readAttempt(slug);
  // Never replace another cart, or undo an explicit edit/empty action.
  if (old) return old.path === order.tracking_path;
  const attempt: Attempt = { content: `recovered:${token}`, key: token, path: order.tracking_path };
  attempts.set(slug, attempt);
  try { localStorage.setItem(storageKey(slug), JSON.stringify(attempt)); } catch { volatileAttempts.add(slug); }
  return true;
}
export function completedRetailCheckout(slug: string) {
  const attempt = readAttempt(slug);
  return attempt?.path ?? (attempt?.predecessor ? `/loja/pedido/${attempt.predecessor}` : undefined);
}
export function isRetailCartEdited(slug: string) { const attempt = readAttempt(slug); return Boolean(attempt?.editing || (attempt?.predecessor && !attempt.path)); }
export function markRetailCartEdited(slug: string) {
  const attempt = readAttempt(slug);
  if (!attempt) return;
  attempt.editing = true; attempt.editing_source = "item_action"; attempts.set(slug, attempt);
  try { localStorage.setItem(storageKey(slug), JSON.stringify(attempt)); volatileAttempts.delete(slug); } catch { volatileAttempts.add(slug); }
}
export function clearRetailCartEdited(slug: string) {
  const attempt = readAttempt(slug);
  if (!attempt?.path) return;
  delete attempt.editing; delete attempt.editing_source; attempts.set(slug, attempt);
  try { localStorage.setItem(storageKey(slug), JSON.stringify(attempt)); volatileAttempts.delete(slug); } catch { volatileAttempts.add(slug); }
}
export async function createRetailOrder(slug: string, payload: RetailPayload): Promise<RetailOrder> {
  const submit = async () => {
    const old = readAttempt(slug);
    const predecessor = old?.editing ? retailCheckoutToken(old.path) ?? old.predecessor : old?.predecessor;
    if (predecessor) payload = { ...payload, replaces_checkout: predecessor };
    // Impressão digital local para retries, sem persistir dados pessoais.
    const bytes = new TextEncoder().encode(JSON.stringify(payload));
    let hash = BigInt("0xcbf29ce484222325");
    for (const byte of bytes) hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * BigInt("0x100000001b3"));
    const content = `${bytes.length}:${hash.toString(16)}`;
    if (old?.path && !old.editing && old.content !== content) throw new ApiError("Compra anterior registrada", 409, { detail: "Confira a tentativa existente antes de pagar novamente." });
    const random = crypto.getRandomValues(new Uint8Array(16));
    random[6] = (random[6] & 15) | 64; random[8] = (random[8] & 63) | 128;
    const hex = Array.from(random, b => b.toString(16).padStart(2, "0")).join("");
    const attempt: Attempt = old?.content === content && !old.editing ? old : { content, predecessor, key: `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}` };
    const save = () => { attempts.set(slug, attempt); try { localStorage.setItem(storageKey(slug), JSON.stringify(attempt)); volatileAttempts.delete(slug); } catch { volatileAttempts.add(slug); } };
    save();
    const order = await apiRequest<RetailOrder>(`/orders/public/${encodeURIComponent(slug)}/`, {
      method: "POST", headers: { "Idempotency-Key": attempt.key }, body: JSON.stringify(payload),
    });
    attempt.path = order.tracking_path;
    save();
    return order;
  };
  return navigator.locks ? navigator.locks.request(`retail-checkout:${slug}`, submit) : submit();
}

export type RetailPrivateSettings = {
  pickup_enabled: boolean; shipping_enabled: boolean; shipping_services: string;
  whatsapp_enabled: boolean; mercado_pago_enabled: boolean; pickup_instructions: string;
  mercado_pago_configured: boolean; mercado_pago_connected: boolean;
  mercado_pago_oauth_configured: boolean; mercado_pago_account: string;
  shipping_configured: boolean;
};
export type RetailPaymentConnection = {
  provider: string; status: string; remote_account_id: string; remote_account_name: string;
};
export async function authorizeRetailMercadoPago() {
  const result = await apiRequest<{ authorization_url: string }>("/retail/payments/authorize/", { authenticated: true, method: "POST" });
  return retailMercadoPagoAuthorizationUrl(result.authorization_url);
}
export function retailMercadoPagoAuthorizationUrl(value: string) {
  const url = new URL(value);
  if (url.protocol !== "https:" || url.hostname !== "auth.mercadopago.com" || url.pathname !== "/authorization" || url.username || url.password)
    throw new ApiError("Não foi possível iniciar a conexão.", 502, null);
  return url.href;
}
